import { Check, CreditCard, Factory, House, Pin, Truck, type LucideIcon } from 'lucide-react'
import { cn } from '../lib/utils'
import type { CarrierShipmentStatus } from '../services/shipping.service'

export type ShipmentStepState = 'completed' | 'progress' | 'upcoming'

export type ShipmentStepKey =
  | 'order_confirmed'
  | 'in_production'
  | 'shipped'
  | 'in_transit'
  | 'delivered'

export type ShipmentStep = {
  key: ShipmentStepKey
  label: string
  icon: LucideIcon
  state: ShipmentStepState
}

export const SHIPMENT_STEPS: Omit<ShipmentStep, 'state'>[] = [
  { key: 'order_confirmed', label: 'Order confirmed', icon: CreditCard },
  { key: 'in_production', label: 'In production', icon: Factory },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'in_transit', label: 'In transit', icon: Pin },
  { key: 'delivered', label: 'Delivered', icon: House },
]

interface ShipmentStatusSources {
  orderConfirmed: boolean
  inProduction: boolean
  shippingStatus: CarrierShipmentStatus | null
}

export function getShipmentSteps({
  orderConfirmed,
  inProduction,
  shippingStatus,
}: ShipmentStatusSources): ShipmentStep[] {
  let currentKey: ShipmentStepKey | null = null

  if (shippingStatus === 'delivered') currentKey = 'delivered'
  else if (shippingStatus === 'in_transit') currentKey = 'in_transit'
  else if (shippingStatus === 'shipped') currentKey = 'shipped'
  else if (inProduction) currentKey = 'in_production'
  else if (orderConfirmed) currentKey = 'order_confirmed'

  const currentIndex = currentKey
    ? SHIPMENT_STEPS.findIndex((step) => step.key === currentKey)
    : -1

  return SHIPMENT_STEPS.map((step, index) => {
    let state: ShipmentStepState = 'upcoming'

    if (currentIndex >= 0) {
      if (currentKey === 'delivered' || index < currentIndex) state = 'completed'
      else if (index === currentIndex) state = 'progress'
    }

    return { ...step, state }
  })
}

export function getCurrentShipmentStep(steps: ShipmentStep[]) {
  return (
    steps.find((step) => step.state === 'progress') ??
    [...steps].reverse().find((step) => step.state === 'completed')
  )
}

interface ShipmentStatusIndicatorProps extends ShipmentStatusSources {
  className?: string
}

export default function ShipmentStatusIndicator({
  orderConfirmed,
  inProduction,
  shippingStatus,
  className,
}: ShipmentStatusIndicatorProps) {
  const steps = getShipmentSteps({ orderConfirmed, inProduction, shippingStatus })
  const allCompleted = steps.every((step) => step.state === 'completed')
  const progressIndex = steps.findIndex((step) => step.state === 'progress')
  const lastCompletedIndex = steps.map((step) => step.state).lastIndexOf('completed')
  const lineIndex = allCompleted
    ? steps.length - 1
    : progressIndex >= 0
      ? progressIndex
      : Math.max(0, lastCompletedIndex)
  const progressRatio = allCompleted || steps.length <= 1
    ? 1
    : lineIndex / (steps.length - 1)

  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-start justify-between relative">
        <div className="absolute top-5 left-[calc(100%/12)] right-[calc(100%/12)] h-0.5 bg-gray-200">
          <div
            className="h-full bg-red-500 transition-all duration-500"
            style={{ width: `${progressRatio * 100}%` }}
          />
        </div>

        {steps.map((step) => {
          const Icon = step.icon

          return (
            <div key={step.key} className="flex flex-1 flex-col items-center relative z-10">
              <div
                className={cn(
                  'flex items-center justify-center w-10 h-10 rounded-full border-2 mb-2 bg-white',
                  step.state === 'completed' && 'bg-red-500 border-red-500',
                  step.state === 'progress' && 'border-red-500 bg-white',
                  step.state === 'upcoming' && 'border-gray-300'
                )}
              >
                {step.state === 'completed' && <Check className="w-5 h-5 text-white" strokeWidth={3} />}
                {step.state === 'progress' && <Icon className="w-5 h-5 text-red-500" />}
                {step.state === 'upcoming' && <Icon className="w-5 h-5 text-gray-400" />}
              </div>

              <p
                className={cn(
                  'text-center text-xs sm:text-sm font-medium leading-tight px-1',
                  step.state === 'upcoming' ? 'text-gray-400' : 'text-gray-900'
                )}
              >
                {step.label}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
